import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Skeleton,
} from '@mui/material';
import { format } from 'date-fns';
import { ReceiptText } from 'lucide-react';

export interface BillingItem {
  id: string;
  amountPaid: number;
  status: string;
  date: string;
  hosted_invoice_url: string;
}

interface CustomerBillingHistoryProps {
  history: BillingItem[];
  isLoading?: boolean;
}

const CustomerBillingHistory: React.FC<CustomerBillingHistoryProps> = ({
  history,
  isLoading = false,
}) => {
  // Number of skeleton rows to show
  const skeletonRows = 2;

  if (isLoading) {
    return (
      <Box mt={6}>
        <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                {['Invoice ID', 'Date', 'Status', 'Amount Paid', 'Receipt'].map(header => (
                  <TableCell key={header}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from(new Array(skeletonRows)).map((_, index) => (
                <TableRow key={index}>
                  {Array.from(new Array(5)).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (!history?.length) {
    return (
      <Box mt={4}>
        <Typography variant="h6" color="textSecondary">
          No billing history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={6}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Billing History
      </Typography>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Amount Paid</TableCell>
              <TableCell>Receipt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map(item => (
              <TableRow key={item.id}>
                <TableCell>{item.id}</TableCell>
                <TableCell>{format(new Date(item.date), 'PPP p')}</TableCell>
                <TableCell>{item.status}</TableCell>
                <TableCell>${(item.amountPaid / 100).toFixed(2)}</TableCell>
                <TableCell>
                  <Link
                    href={item.hosted_invoice_url}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <ReceiptText size={18} />
                    View
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerBillingHistory;
